import React from "react";
import { AsyncState } from "./use_async";

export type AsyncFn<Result = any, Args extends any[] = any[]> = [
  AsyncState<Result>,
  (...args: Args | []) => Promise<Result>
];

export function useAsyncFn<Result = any, Args extends any[] = any[]>(
  fn: (...args: Args | []) => Promise<Result>,
  deps: React.DependencyList = [],
  initialState: AsyncState<Result> = { loading: false }
): AsyncFn<Result, Args> {
  const lastCallId = React.useRef(0);
  const [state, setState] = React.useState<AsyncState<Result>>(initialState);

  const callback = React.useCallback(async (...args: Args | []) => {
    const callId = ++lastCallId.current;

    setState({ loading: true });

    try {
      const data = await fn(...args);

      if (callId === lastCallId.current) {
        setState({ data, loading: false });
      }

      return data as Result;
    } catch (error) {
      if (callId === lastCallId.current) {
        setState({ error: error as Error, loading: false });
      }
      throw error;
    }
    // eslint-disable-next-line
  }, deps);

  return [state, callback];
}
